import path from 'node:path';
import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import { DriveRollup } from 'components/cards';
import Counter from 'components/counter';
import Layout from 'components/layout';
import Anchor from 'components/typography/anchor';
import driveShareImg from 'images/shares/drives.png';
import { getDriveAccumulationForState } from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import { driveRollupToUserContentSlim } from 'lib/transformers';
import PropTypes from 'prop-types';
import { DATA_TYPES } from 'siteconfig';
import slugify from 'slugify';
import { CampaignTypeDriveRollupList } from 'utilities/propTypes';
import {
  getStateProperName,
  isValidStateCode,
  STATE_POSTAL_CODES,
} from 'utilities/states';

const REVALIDATE_TIME_MINUTES = 1;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;

import EmptyResults from 'components/Campaign/EmptyResults';

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ params }) => {
    const stateCode = params.statecode.toUpperCase();

    const parentCtx = otelApi.context.active();
    const parentSpan = otelApi.trace
      .getTracer('Gin Rickey')
      .startSpan('candidates-for-state', undefined, parentCtx);

    parentSpan.setAttributes({
      // https here is unused, only need it to split the URL open
      [ATTR_HTTP_ROUTE]: `/drives/explore/${stateCode}`,
      'state.code': stateCode,
    });

    if (!isValidStateCode(stateCode)) {
      parentSpan.setStatus({
        code: SpanStatusCode.OK,
      });
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });
      parentSpan.end();
      return {
        notFound: true,
      };
    }

    const dataCandidate =
      (await getDriveAccumulationForState({
        driveType: 'CANDIDATE',
        stateCode,
        limit: 15,
      })) || {};

    const candidates = dataCandidate?.candidates || [];
    const totalSigns = dataCandidate?.totalSigns || 0;

    const candidatesRollup = [];

    candidates.forEach((c) => {
      candidatesRollup.push(
        driveRollupToUserContentSlim({ apiDriveRollup: c })
      );
    });
    // Highest to lowest
    candidatesRollup.sort((a, b) => b.signCount - a.signCount);

    parentSpan.end();

    return {
      props: {
        candidates: candidatesRollup,
        totalSigns,
        stateCode,
      },
      revalidate: REVALIDATE_TIME_SECONDS,
    };
  }
);

const OfficeFilter = ({ active = false }) => {
  return active ? (
    <div>
      <Anchor href="">Senate</Anchor>
      <Anchor href="">Congress</Anchor>
      <Anchor href="">State Senate</Anchor>
      <Anchor href="">State House</Anchor>
      <Anchor href="">Local</Anchor>
    </div>
  ) : null;
};

const Drives = ({ candidates, totalSigns, stateCode }) => {
  const candidateSection = candidates.map((c) => (
    <DriveRollup
      key={c.candidateId}
      driveRollup={c}
      linkPath={path.join(
        DATA_TYPES.CANDIDATE.basePath,
        `${c.candidateId}/${slugify(c.candidateName)}`
      )}
      type={DATA_TYPES.CANDIDATE}
    />
  ));

  return (
    <Layout
      title={`${getStateProperName(stateCode)} Vote Drives`}
      description={`Voter turnout drives for candidates in ${getStateProperName(
        stateCode
      )} created by Resistbot members.`}
      image={driveShareImg}
      imageAltText='Illustration of a bot voting with overlay text that reads, "Vote Drives: Endorse your candidate and get out the vote before Election Day. Text drive to 50409.'
    >
      <div className="flex flex-row md:flex-col md:text-center">
        <div className="flex-1 md:mr-9">
          <ol className="mb-4 flex list-none flex-wrap items-center gap-2 p-0 text-sm">
            <li>
              <Anchor href="/drives">United States</Anchor>
            </li>
            <li>{getStateProperName(stateCode)}</li>
          </ol>
          <h1>
            {getStateProperName(stateCode)} Drives <em>by</em> Candidate
          </h1>
          <p className="text-sm">
            Tap a candidate to see the drives members have created.
          </p>
        </div>
        <div>
          <Counter count={totalSigns} text="Total Pledges" />
        </div>
      </div>

      {/* TODO: Hook this up when there's a query for it */}
      <OfficeFilter />

      {/* Currently this displays _all_ candidates we have drives for (in this state). */}
      {candidates.length < 1 ? (
        <EmptyResults type={DATA_TYPES.DRIVE} />
      ) : (
        candidateSection
      )}
    </Layout>
  );
};

Drives.propTypes = {
  candidates: PropTypes.arrayOf(CampaignTypeDriveRollupList),
  totalSigns: PropTypes.number.isRequired,
  stateCode: PropTypes.oneOf(Array.from(STATE_POSTAL_CODES)),
};

export default Drives;
